/**
 * `/screen-print` — the screen-print RUN worklist, and the office view of the JOB PACKET.
 *
 * A run is a FILMLESS batch: no gang sheet, no film, no compositing. It lives on its own page rather
 * than on `/batches` for three reasons — that page is titled "Gang sheets" and a run is not one; its
 * card is built around sheet preview / density / roll / print-ready downloads, every one of which is
 * absent here; and the sidebar already gives each production lane its own row (Caps, Ship-only).
 *
 * The list answers "which runs, how far along"; the detail is the packet (`screen-print-packet.tsx`,
 * plan § 4.2): the screens with their recipe in head order, the press carousel, the mockup, the master
 * to download, ink & cure, pallet & placement, blanks to pull, press progress — plus the office-only
 * actions (pick list, labels, mark printed, delete). The kiosk (P3b) renders the same packet without them.
 *
 * ⚠️ F1: the Screens column is Σ colours across the run's setups, and only once EVERY design on the run
 * has a screen spec. Until then it reads "—" — a setup count relabelled "screens" was wrong 3× on
 * multi-colour art, and this page is what tells the operator how many screens to burn.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  ClipboardCheck,
  ClipboardList,
  Inbox,
  Loader2,
  Printer,
  RefreshCw,
  Tags,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageContainer } from '@/components/page-container';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import {
  BATCH_STATUS_META,
  type BatchPulledVia,
  batchLabel,
  blanksToPull,
  pressedGarmentCount,
  pullState,
} from './batch-lib';
import { DeleteBatchDialog, MarkPrintedControl } from './batches-page';
import { LabelSheet } from './label-sheet';
import { relativeAge } from './orders-queue';
import { PullChip } from './pull-chip';
import { PullList } from './pull-list';
import {
  type BatchSummary,
  runFullyPressed,
  runGarmentCount,
  runSetups,
  screenPrintRuns,
} from './screen-print-lib';
import { RunPacket, useDesignsById } from './screen-print-packet';
import { joinSpecs, runScreenCount } from './screen-print-packet-lib';
import { useBlankNames } from './use-blank-names';

const PAGE_LIMIT = 100;

export function ScreenPrintPage() {
  const client = useApiClient();
  const nameBySku = useBlankNames();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // ?run=<batchId> deep link (plan § 4.1) — the `/batches?batch=` pattern: a job ticket's QR and the
  // `/press` card (P6) open a run's packet directly. The list drains every page, so the run is on it.
  const urlRun = (useSearch({ strict: false }) as { run?: string }).run;
  useEffect(() => {
    if (urlRun) setSelectedId(urlRun);
  }, [urlRun]);
  // The whole design library, once — the same cache entry the library page uses — so the Screens column
  // can join every run's setups to their specs without a request per run.
  const designs = useDesignsById();

  const query = useInfiniteQuery({
    queryKey: ['batches', 'screen-print-runs'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/batches', {
        params: { query: { limit: PAGE_LIMIT, cursor: pageParam } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load runs.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Drain every page: `kind` is not a server-side filter, so an undrained list would hide runs behind
  // gang sheets rather than showing an empty state honestly.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flattened here rather than through `flattenBatchPages` — that helper is narrowly typed for the
  // compositing-poll selector and erases everything but `status`, which is exactly the field this page
  // does NOT filter on.
  const runs = useMemo(
    () => screenPrintRuns(query.data?.pages.flatMap((p) => p.items) ?? []),
    [query.data],
  );
  const selected = runs.find((r) => r.batchId === selectedId) ?? null;

  return (
    <PageContainer>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Screen print</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Runs produced off the gang sheet — no film. Burn the screens, pull the blanks, press
            each location, then close the run.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          {query.isFetching ? (
            <Loader2 className="mr-1.5 size-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 size-4" />
          )}
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : runs.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Inbox className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">No screen-print runs</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Filter{' '}
            <Link to="/orders" className="underline">
              Orders
            </Link>{' '}
            by print method, select the screen-print lines and Build — the run lands here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Run</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Garments</th>
                <th className="px-3 py-2 text-right font-medium">Setups</th>
                <th className="px-3 py-2 text-right font-medium">Screens</th>
                <th className="px-3 py-2 text-right font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const work = run.screenPrintWork ?? [];
                const meta = BATCH_STATUS_META[run.status];
                const setups = runSetups(work);
                const screens =
                  designs.isLoading || designs.isError
                    ? undefined
                    : runScreenCount(joinSpecs(setups, designs.byId));
                return (
                  <tr
                    key={run.batchId}
                    onClick={() => setSelectedId(run.batchId)}
                    className={cn(
                      'cursor-pointer border-t [&:nth-child(even)]:bg-muted/20 hover:bg-muted/40',
                      selectedId === run.batchId && 'bg-muted/50',
                    )}
                  >
                    <td className="px-3 py-2 align-top text-xs">
                      <span className="font-medium">{batchLabel(run)}</span>
                      <div className="mt-0.5 text-muted-foreground">
                        {run.brandLabel ?? run.storeIds.join(', ')}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge variant="outline" className={meta.className}>
                        {meta.label}
                      </Badge>
                      {runFullyPressed(work) && run.status === 'READY' && (
                        <Badge
                          variant="outline"
                          className="mt-1 block w-fit border-emerald-300 bg-emerald-50 font-normal text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
                        >
                          all pressed
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums">
                      {runGarmentCount(work)}
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums text-muted-foreground">
                      {setups.length}
                    </td>
                    <td
                      className="px-3 py-2 text-right align-top tabular-nums"
                      title={
                        screens == null
                          ? designs.isLoading
                            ? 'Loading the design specs…'
                            : 'A design on this run has no screen spec yet — its colours are not counted.'
                          : `${screens} screen${screens === 1 ? '' : 's'} to burn`
                      }
                    >
                      {screens ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td
                      className="px-3 py-2 text-right align-top text-xs text-muted-foreground"
                      title={new Date(run.createdAt).toLocaleString()}
                    >
                      {relativeAge(run.createdAt, Date.now())}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <RunDetail
          run={selected}
          nameBySku={nameBySku}
          onChanged={() => query.refetch()}
          onDeleted={() => {
            setSelectedId(null);
            void query.refetch();
          }}
        />
      )}
    </PageContainer>
  );
}

/**
 * The office's run detail: the packet plus the actions the floor does not get on the kiosk. Every
 * action here has been missing once and reported from the live dashboard (see the page test), so the
 * set is pinned by name there.
 */
function RunDetail({
  run,
  nameBySku,
  onChanged,
  onDeleted,
}: {
  run: BatchSummary;
  nameBySku: ReturnType<typeof useBlankNames>;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const work = run.screenPrintWork ?? [];
  // ⚠️ The printable pull list and the hem-tag labels are the two artifacts the floor actually carries,
  // and shipping the page without them was the owner's first report of it. Both are the SAME overlays
  // the gang-sheet page uses — a run is the same batch record, so they need no run-specific variant.
  // Pulled-blanks v2: the overlay is the pull station; `station` is which door opened it (`via`).
  const [station, setStation] = useState<BatchPulledVia | null>(null);
  const [showLabels, setShowLabels] = useState(false);
  // A filmless run's pull rows come from its screen-print work alone — no manifest needed for the chip.
  const pull = pullState(run, blanksToPull({ transfers: [] }, nameBySku, work));
  const pullIncomplete = pull.kind !== 'pulled' || pull.complete === false;
  const [deleteOpen, setDeleteOpen] = useState(false);
  // ⚠️ Same gate the gang-sheet card uses. The server allows DRAFT/READY/FAILED and refuses a PRINTED
  // batch (undo the close first) — mirrored here so the button is disabled rather than 409-ing.
  const canDelete = run.status !== 'COMPOSITING' && run.status !== 'PRINTED';
  // Every placement of a run is screen print, so "screen print pending" is simply "not all pressed" —
  // and `mark-printed` is gated on it exactly as it is for a mixed sheet.
  const screenPrintPending = !runFullyPressed(work);

  return (
    <>
      <RunPacket
        run={run}
        nameBySku={nameBySku}
        leading={
          <>
            {(run.status === 'READY' || run.status === 'PRINTED') && (
              <PullChip state={pull} status={run.status} now={Date.now()} />
            )}
            <Button size="sm" variant="outline" onClick={() => setStation('pull-list')}>
              <ClipboardList className="mr-1.5 size-4" />
              Pull list
            </Button>
            {run.status === 'READY' && pullIncomplete && (
              <Button size="sm" variant="outline" onClick={() => setStation('run')}>
                <ClipboardCheck className="mr-1.5 size-4" />
                Tick the pull list
              </Button>
            )}
            {pull.kind === 'pulled' && !pullIncomplete && (
              <Button size="sm" variant="outline" onClick={() => setStation('run')}>
                <ClipboardCheck className="mr-1.5 size-4" />
                Edit rows
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowLabels(true)}>
              <Tags className="mr-1.5 size-4" />
              Print labels
            </Button>
          </>
        }
        trailing={
          <>
            <Button asChild size="sm" variant="outline">
              <Link to="/press">
                <Printer className="mr-1.5 size-4" />
                Press station
              </Link>
            </Button>
            <MarkPrintedControl
              batchId={run.batchId}
              status={run.status}
              screenPrintLocations={run.screenPrintLocations}
              screenPrintPending={screenPrintPending}
              screenPrintWork={work}
              pulled={run.pulled}
              pulledRev={run.pulledRev}
              onChanged={onChanged}
            />
            {/* ⚠️ Shipped without this and the owner could not delete a TEST run at all: `/batches`
                excludes runs, so this panel is the ONLY route to it. The server has always handled it —
                PR-4's first fix made the unbuild reopen a filmless run's lines — it was purely a missing
                affordance. */}
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={!canDelete}
              title={
                canDelete
                  ? undefined
                  : run.status === 'PRINTED'
                    ? 'Run is printed — undo “Mark printed” first.'
                    : 'Run is busy.'
              }
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="mr-1.5 size-4" />
              Delete run
            </Button>
          </>
        }
      />

      <DeleteBatchDialog
        batchId={run.batchId}
        lineCount={run.lineCount}
        pulledUnits={run.pulledUnits ?? 0}
        pressedGarments={pressedGarmentCount([], work)}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onDeleted}
      />

      {showLabels && (
        <LabelSheet
          batchId={run.batchId}
          screenPrintWork={work}
          batchTitle={batchLabel(run)}
          onClose={() => setShowLabels(false)}
        />
      )}
      {/* No "Reprint a transfer" here: a filmless run has no film to pick — a ruined garment is reported
          from the press station, which names the shirt, not a transfer. */}
      {station && (
        <PullList
          batchId={run.batchId}
          label={batchLabel(run)}
          screenPrintWork={work}
          via={station}
          onClose={() => setStation(null)}
        />
      )}
    </>
  );
}
